import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

/**
 * Questionnaire profil investisseur - Simplifié à 3 questions
 * Posé après la première connexion
 */
const InvestorProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userProfile, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    primary_goal: '',
    risk_tolerance: '',
    investment_timeline: ''
  });

  // Check if coming from demo signup flow
  const fromDemo = searchParams?.get('fromDemo') === 'true';
  const pendingPropertyId = localStorage.getItem('pendingPropertyId');

  // Pre-fill if profile already has data
  useEffect(() => {
    if (userProfile) {
      setFormData({
        primary_goal: userProfile.primary_goal || '',
        risk_tolerance: userProfile.risk_tolerance || '',
        investment_timeline: userProfile.investment_timeline || ''
      });
    }
  }, [userProfile]);

  const handleSelect = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const canSave = formData.primary_goal && formData.risk_tolerance && formData.investment_timeline;

  const saveProfile = async () => {
    if (!canSave) return;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          primary_goal: formData.primary_goal,
          risk_tolerance: formData.risk_tolerance,
          investment_timeline: formData.investment_timeline,
          investor_profile_completed: true
        })
        .eq('id', user.id);

      if (error) throw error;
      
      await refreshProfile();
      
      // Redirect to analysis results if coming from demo, otherwise to dashboard
      if (fromDemo && pendingPropertyId) {
        localStorage.removeItem('pendingPropertyId');
        navigate(`/analysis-results?propertyId=${pendingPropertyId}`, { replace: true });
      } else {
        navigate('/main-dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Error saving investor profile:', err);
      alert('Erreur lors de la sauvegarde. Veuillez réessayer.');
    } finally {
      setSaving(false);
    }
  };

  const Option = ({ selected, onClick, icon, title, subtitle }) => (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        padding: 'var(--spacing-md)',
        background: selected ? 'var(--color-primary)' : 'var(--color-surface)',
        border: `2px solid ${selected ? 'var(--color-primary)' : 'var(--color-border)'}`,
        borderRadius: 'var(--radius-lg)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-md)'
      }}
    >
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: 'var(--radius-md)',
        background: selected ? 'rgba(255,255,255,0.2)' : 'var(--color-muted)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <Icon name={icon} size={20} color={selected ? 'white' : 'var(--color-primary)'} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{
          fontWeight: 600,
          fontSize: '0.9375rem',
          color: selected ? 'white' : 'var(--color-foreground)',
          marginBottom: '2px'
        }}>
          {title}
        </p>
        {subtitle && (
          <p style={{
            fontSize: '0.8125rem',
            color: selected ? 'rgba(255,255,255,0.8)' : 'var(--color-muted-foreground)'
          }}>
            {subtitle}
          </p>
        )}
      </div>
      {selected && <Icon name="Check" size={20} color="white" />}
    </button>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-background)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <header style={{
        padding: 'var(--spacing-lg)',
        textAlign: 'center',
        borderBottom: '1px solid var(--color-border)'
      }}>
        <h1 style={{
          fontSize: '1.25rem',
          fontWeight: 600,
          color: 'var(--color-foreground)'
        }}>
          Personnalisez vos analyses
        </h1>
        <p style={{
          fontSize: '0.875rem',
          color: 'var(--color-muted-foreground)',
          marginTop: 'var(--spacing-xs)'
        }}>
          3 questions rapides pour adapter nos recommandations
        </p>
      </header>

      {/* Questions */}
      <main style={{
        flex: 1,
        maxWidth: '500px',
        margin: '0 auto',
        padding: 'var(--spacing-xl) var(--spacing-lg)',
        width: '100%'
      }}>
        {/* Question 1: Objectif principal */}
        <div style={{ marginBottom: 'var(--spacing-xl)' }}>
          <p style={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            marginBottom: 'var(--spacing-md)',
            color: 'var(--color-foreground)'
          }}>
            1. Quel est votre objectif principal?
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            <Option
              selected={formData.primary_goal === 'cashflow'}
              onClick={() => handleSelect('primary_goal', 'cashflow')}
              icon="DollarSign"
              title="Cashflow"
              subtitle="Revenus mensuels réguliers"
            />
            <Option
              selected={formData.primary_goal === 'appreciation'}
              onClick={() => handleSelect('primary_goal', 'appreciation')}
              icon="TrendingUp"
              title="Croissance"
              subtitle="Appréciation à long terme"
            />
            <Option
              selected={formData.primary_goal === 'balanced'}
              onClick={() => handleSelect('primary_goal', 'balanced')}
              icon="Scale"
              title="Équilibre"
              subtitle="Un peu des deux"
            />
          </div>
        </div>

        {/* Question 2: Tolérance au risque */}
        <div style={{ marginBottom: 'var(--spacing-xl)' }}>
          <p style={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            marginBottom: 'var(--spacing-md)',
            color: 'var(--color-foreground)'
          }}>
            2. Quelle est votre tolérance au risque?
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            <Option
              selected={formData.risk_tolerance === 'conservative'}
              onClick={() => handleSelect('risk_tolerance', 'conservative')}
              icon="Shield"
              title="Faible"
              subtitle="Je préfère la sécurité"
            />
            <Option
              selected={formData.risk_tolerance === 'moderate'}
              onClick={() => handleSelect('risk_tolerance', 'moderate')}
              icon="Activity"
              title="Modérée"
              subtitle="Un équilibre risque/rendement"
            />
            <Option
              selected={formData.risk_tolerance === 'aggressive'}
              onClick={() => handleSelect('risk_tolerance', 'aggressive')}
              icon="Zap"
              title="Élevée"
              subtitle="Je vise les meilleurs rendements"
            />
          </div>
        </div>

        {/* Question 3: Horizon d'investissement */}
        <div style={{ marginBottom: 'var(--spacing-xl)' }}>
          <p style={{
            fontSize: '0.9375rem',
            fontWeight: 600,
            marginBottom: 'var(--spacing-md)',
            color: 'var(--color-foreground)'
          }}>
            3. Quel est votre horizon d'investissement?
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            <Option
              selected={formData.investment_timeline === 'immediate'}
              onClick={() => handleSelect('investment_timeline', 'immediate')}
              icon="Zap"
              title="Court terme"
              subtitle="Je veux investir rapidement"
            />
            <Option
              selected={formData.investment_timeline === '1_year'}
              onClick={() => handleSelect('investment_timeline', '1_year')}
              icon="Calendar"
              title="Moyen terme"
              subtitle="Dans les 12 prochains mois"
            />
            <Option
              selected={formData.investment_timeline === 'exploring'}
              onClick={() => handleSelect('investment_timeline', 'exploring')}
              icon="Search"
              title="Long terme"
              subtitle="J'explore mes options"
            />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={{
        padding: 'var(--spacing-lg)',
        borderTop: '1px solid var(--color-border)',
        background: 'var(--color-surface)'
      }}>
        <div style={{ maxWidth: '500px', margin: '0 auto', display: 'flex', gap: 'var(--spacing-md)' }}>
          <Button
            variant="ghost"
            size="lg"
            onClick={() => {
              if (fromDemo && pendingPropertyId) {
                localStorage.removeItem('pendingPropertyId');
                navigate(`/analysis-results?propertyId=${pendingPropertyId}`, { replace: true });
              } else {
                navigate('/main-dashboard', { replace: true });
              }
            }}
            style={{ flex: 1 }}
          >
            Passer
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={saveProfile}
            disabled={!canSave || saving}
            loading={saving}
            style={{ flex: 2 }}
          >
            Continuer
          </Button>
        </div>
      </footer>
    </div>
  );
};

export default InvestorProfile;
